import { SourceType } from 'types/SourceType'
import { isValidIssueType } from '../../components/entities/IssueType'
import { IssueType } from 'types/IssueEntity'
import { TransactionType } from 'types/TransactionType'

const validLinkedSourceTypes: SourceType[] = [
    'Publication',
    'Terms',
    'New commitments',
    'Update',
    'Outcome',
    'Stabilisation',
    'Cancellation',
    'Conversion',
    'Offset',
    'Repayment',

    // Mentioned earlier, but not used:
    // 'Updated terms',
    // 'Updated schedule',
]
const validUploadedSourceTypes: SourceType[] = ['Prospectus', 'Memorandum', 'Supplement', 'InformationDocument']
// These source types are singular, ie there can only be one of each type
export const singularIssueSourceTypes: SourceType[] = [
    'Publication',
    'Terms',
    'Cancellation',
    'Outcome',
    'Memorandum',
    'Prospectus',
]
export const singularWarrantSourceTypes: SourceType[] = ['Publication', 'Cancellation', 'Memorandum', 'Prospectus']
export const singularConvertibleSourceTypes: SourceType[] = ['Publication']

export const isSingularIssueSourceType = (type: SourceType) => singularIssueSourceTypes.includes(type)
export const isSingularWarrantSourceType = (type: SourceType) => singularWarrantSourceTypes.includes(type)

export const getValidLinkedSourceTypes = () => validLinkedSourceTypes
export const getValidUploadedSourceTypes = () => validUploadedSourceTypes

/**
 * Retrieves a list of valid source types based on the provided issue type.
 * If no issue type is specified, returns all valid linked and uploaded source types.
 * Throws an error if the provided issue type is invalid.
 *
 * @param {string|null} transactionType Optional parameter specifying the type of issue.
 * @returns {string[]} An array of valid source types corresponding to the specified issue type.
 * @throws {Error} If the issue type is not valid.
 */
export const getValidSourceTypes = (transactionType: TransactionType | null = null): SourceType[] => {
    if (!transactionType) return [...validLinkedSourceTypes, ...validUploadedSourceTypes]
    if (transactionType !== 'Warrant' && transactionType !== 'Convertible' && !isValidIssueType(transactionType))
        throw new Error('Invalid transactionType: ' + transactionType)
    if (transactionType === 'ABB Directed issue') return ['Cancellation', 'Outcome', 'Publication']
    if (transactionType === 'Directed issue') return ['Cancellation', 'Update', 'Outcome', 'Prospectus', 'Publication']
    if (transactionType === 'Guarantor fee issue') return ['Publication']

    if (transactionType === 'IPO')
        return ['Update', 'Stabilisation', 'Memorandum', 'Outcome', 'Prospectus', 'Publication', 'Supplement']
    if (transactionType === 'Non-cash issue') return ['Cancellation', 'Update', 'Outcome', 'Prospectus', 'Publication']
    // if (issueType === 'Other') return ['Publication']

    if (transactionType === 'Rights issue')
        return [
            'Cancellation',
            'Update',
            'Memorandum',
            'New commitments',
            'Outcome',
            'Prospectus',
            'Publication',
            'Supplement',
            'Terms',
            'InformationDocument',
        ]
    if (transactionType === 'Warrant') return ['Update', 'New commitments', 'Outcome', 'Publication', 'Terms']
    if (transactionType === 'Convertible') return ['Publication', 'Update', 'Conversion', 'Offset', 'Repayment']

    throw new Error('Unknown issueType: ' + (transactionType as string))
}
const isValidLinkedSourceType = (type: SourceType) => validLinkedSourceTypes.includes(type)
const isValidUploadedSourceType = (type: SourceType) => validUploadedSourceTypes.includes(type)
export const isValidSourceType = (sourceType: SourceType, issueType: IssueType | 'Warrant' | null = null) =>
    issueType
        ? getValidSourceTypes(issueType).includes(sourceType)
        : isValidLinkedSourceType(sourceType) || isValidUploadedSourceType(sourceType)
